import { writable } from "svelte/store"
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
} from "aws-amplify/auth"

const createAdminStore = () => {
  const { subscribe, update, set } = writable({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
    tokens: null,
  })

  return {
    subscribe,
    async init() {
      update((state) => ({ ...state, isLoading: true }))

      try {
        const user = await getCurrentUser()
        const session = await fetchAuthSession()

        if (user && session.tokens) {
          const userInfo = {
            username: user.username,
            attributes: user.signInDetails || {},
          }

          const tokens = {
            accessToken:
              session.tokens.accessToken &&
              session.tokens.accessToken.toString(),
            idToken:
              session.tokens.idToken && session.tokens.idToken.toString(),
            refreshToken:
              session.tokens.refreshToken &&
              session.tokens.refreshToken.toString(),
          }

          update((state) => ({
            ...state,
            isAuthenticated: true,
            user: userInfo,
            tokens,
            isLoading: false,
            error: null,
          }))
        } else {
          throw new Error("No valid session")
        }
      } catch (error) {


        update((state) => ({
          ...state,
          isAuthenticated: false,
          user: null,
          tokens: null,
          isLoading: false,
          error: null,
        }))
      }
    },

    async login(username, password) {
      update((state) => ({ ...state, isLoading: true, error: null }))

      try {
        const result = await signIn({ username, password })

        if (
          result.nextStep &&
          result.nextStep.signInStep ===
            "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          update((state) => ({
            ...state,
            isLoading: false,
            error: {
              type: "NEW_PASSWORD_REQUIRED",
              message: "初回ログインのため、新しいパスワードを設定してください",
              user: result,
            },
          }))
          return { success: false, requireNewPassword: true, user: result }
        }

        // セッション取得をリトライ付きで実行
        let session, user
        try {
          session = await fetchAuthSession()
          user = await getCurrentUser()
        } catch (sessionError) {
          console.warn("First session fetch failed, retrying...", sessionError)
          // 短時間待機してリトライ
          await new Promise(resolve => setTimeout(resolve, 1000))
          session = await fetchAuthSession()
          user = await getCurrentUser()
        }

        const userInfo = {
          username: user.username,
          attributes: user.signInDetails || {},
        }

        const tokens = {
          accessToken:
            session.tokens &&
            session.tokens.accessToken &&
            session.tokens.accessToken.toString(),
          idToken:
            session.tokens &&
            session.tokens.idToken &&
            session.tokens.idToken.toString(),
          refreshToken:
            session.tokens &&
            session.tokens.refreshToken &&
            session.tokens.refreshToken.toString(),
        }

        update((state) => ({
          ...state,
          isAuthenticated: true,
          user: userInfo,
          tokens,
          isLoading: false,
          error: null,
        }))

        return { success: true }
      } catch (error) {
        console.error("Login error:", error)
        let errorMessage = "ログインに失敗しました"

        if (error.name === "NotAuthorizedException") {
          errorMessage = "ユーザー名またはパスワードが正しくありません"
        } else if (error.name === "UserNotFoundException") {
          errorMessage = "ユーザーが見つかりません"
        } else if (error.name === "InvalidParameterException") {
          errorMessage = "パラメータが無効です"
        } else if (error.name === "UnexpectedSignInInterruptionException") {
          errorMessage = "認証セッションに問題があります。再度お試しください"
        }

        update((state) => ({
          ...state,
          isLoading: false,
          error: { type: "LOGIN_ERROR", message: errorMessage },
        }))

        return { success: false, error: errorMessage }
      }
    },

    async setNewPassword(newPassword) {
      update((state) => ({ ...state, isLoading: true, error: null }))

      try {
        await confirmSignIn({
          challengeResponse: newPassword,
        })

        const session = await fetchAuthSession()
        const currentUser = await getCurrentUser()

        const userInfo = {
          username: currentUser.username,
          attributes: currentUser.signInDetails || {},
        }

        const tokens = {
          accessToken:
            session.tokens &&
            session.tokens.accessToken &&
            session.tokens.accessToken.toString(),
          idToken:
            session.tokens &&
            session.tokens.idToken &&
            session.tokens.idToken.toString(),
          refreshToken:
            session.tokens &&
            session.tokens.refreshToken &&
            session.tokens.refreshToken.toString(),
        }

        update((state) => ({
          ...state,
          isAuthenticated: true,
          user: userInfo,
          tokens,
          isLoading: false,
          error: null,
        }))

        return { success: true }
      } catch (error) {
        console.error("Set new password error:", error)
        let errorMessage = "新しいパスワードの設定に失敗しました"

        if (error.name === "InvalidPasswordException") {
          errorMessage =
            "パスワードが条件を満たしていません（8文字以上、大文字・小文字・数字を含む）"
        }

        update((state) => ({
          ...state,
          isLoading: false,
          error: { type: "PASSWORD_ERROR", message: errorMessage },
        }))

        return { success: false, error: errorMessage }
      }
    },

    async logout() {
      update((state) => ({ ...state, isLoading: true }))

      try {
        await signOut()

        update((state) => ({
          isAuthenticated: false,
          user: null,
          tokens: null,
          isLoading: false,
          error: null,
        }))

        return { success: true }
      } catch (error) {
        console.error("Logout error:", error)

        update((state) => ({
          isAuthenticated: false,
          user: null,
          tokens: null,
          isLoading: false,
          error: null,
        }))

        return { success: true } // ローカルクリアは成功とみなす
      }
    },

    clearError() {
      update((state) => ({ ...state, error: null }))
    },

    setError(type, message) {
      update((state) => ({
        ...state,
        error: { type, message },
      }))
    },

    async refreshToken() {
      try {
        const session = await fetchAuthSession({ forceRefresh: true })
        if (session.tokens) {
          const tokens = {
            accessToken:
              session.tokens.accessToken &&
              session.tokens.accessToken.toString(),
            idToken:
              session.tokens.idToken && session.tokens.idToken.toString(),
            refreshToken:
              session.tokens.refreshToken &&
              session.tokens.refreshToken.toString(),
          }

          update((state) => ({ ...state, tokens }))
          return tokens
        }
      } catch (error) {
        console.error("Token refresh error:", error)
        this.logout()
      }
      return null
    },
  }
}

export const adminAuth = createAdminStore()
